import {
  atomFactorOf,
  CreateInventoryChange,
  EntityTypes,
  formatCurrency,
  FractionCountItem,
  InventoryChangeReason,
  Merchant,
  onHandOf,
  Store,
  StoreProduct,
  StoreProductFraction,
} from '@bofrak-backend/shared';
import {
  Badge,
  Box,
  Button,
  ButtonGroup,
  Flex,
  Grid,
  GridItem,
  IconButton,
  Image,
  Input,
  Radio,
  RadioGroup,
  Stack,
  Text,
  Tooltip,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { isAxiosError } from 'axios';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiHash, FiLayers, FiList } from 'react-icons/fi';
import { useMutation } from 'react-query';
import { productDb } from '../../store-products/db';
import { apiAdapter } from '../../utils';
import { useSharedUiTranslation } from '../../i18n';
import { ProductDriftAlert } from './ProductDriftAlert';
import {
  countScaleOf,
  decomposeCount,
  roundQty,
  sumCountAtoms,
} from './quick-count-math';

type ViewMode = 'total' | 'grouped' | 'detailed';
const VIEW_MODE_KEY = 'qic.viewMode';

type BreakdownRow = { id: string; name: string; units: number };

const TotalLine: React.FC<{
  value: number;
  unit: string;
  signed?: boolean;
}> = ({ value, unit, signed }) => {
  const color = signed
    ? value < 0
      ? 'red.600'
      : value > 0
        ? 'green.700'
        : 'gray.800'
    : 'gray.800';
  const prefix = signed && value > 0 ? '+' : '';
  return (
    <Flex justify="space-between" fontSize="xs" gap={2}>
      <Text color="gray.600" noOfLines={1}>
        {unit}
      </Text>
      <Text
        fontWeight="semibold"
        color={color}
        sx={{ fontVariantNumeric: 'tabular-nums' }}>
        {prefix}
        {value}
      </Text>
    </Flex>
  );
};

const BreakdownList: React.FC<{
  breakdown: BreakdownRow[];
  /** When true, prefix non-zero units with + / − and color them. */
  signed?: boolean;
}> = ({ breakdown, signed }) => {
  const nonZero = breakdown.filter((b) => b.units !== 0);

  if (nonZero.length === 0) {
    return (
      <Text fontSize="xs" color="gray.400">
        —
      </Text>
    );
  }

  return (
    <VStack align="stretch" spacing={0}>
      {nonZero.map((b) => {
        const color = signed
          ? b.units < 0
            ? 'red.600'
            : 'green.700'
          : 'gray.800';
        const prefix = signed && b.units > 0 ? '+' : '';
        return (
          <Flex key={b.id} justify="space-between" fontSize="xs" gap={2}>
            <Text color="gray.600" noOfLines={1}>
              {b.name}
            </Text>
            <Text
              fontWeight="semibold"
              color={color}
              sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {prefix}
              {b.units}
            </Text>
          </Flex>
        );
      })}
    </VStack>
  );
};

export interface QuickInventoryCountProps {
  item: StoreProduct;
  onClose: () => void;
  onCounted?: () => void;
  /** Called after a successful inventory update so the consuming app can refresh its own caches */
  onSuccess?: (productId: string) => void;
  merchant: Merchant;
  store: Store;
  source: { id: string; name: string };
  /** Show a "History" button that triggers onViewHistory */
  showHistoryButton?: boolean;
  onViewHistory?: (item: StoreProduct) => void;
  /** When true, hide expected stock, change, cost difference, and history (blind counting) */
  blindMode?: boolean;
  /** Called before submitting the inventory change. Receives the new actual stock.
   *  If it throws/rejects, the submission is aborted. */
  onBeforeSubmit?: (actualStock: number) => Promise<void>;
}

export const QuickInventoryCount: React.FC<QuickInventoryCountProps> = ({
  item,
  onClose,
  onCounted,
  onSuccess,
  merchant,
  store,
  source,
  showHistoryButton,
  onViewHistory,
  blindMode,
  onBeforeSubmit,
}) => {
  const [reason, setReason] = useState(InventoryChangeReason.INVENTORY_COUNT);
  // A count finds everything on the shelf, held stock included.
  const stock = item ? onHandOf(item) : 0;
  const toast = useToast();
  const { t } = useSharedUiTranslation();

  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    if (typeof window === 'undefined') return 'detailed';
    const stored = window.localStorage.getItem(VIEW_MODE_KEY);
    return stored === 'total' || stored === 'detailed' || stored === 'grouped'
      ? stored
      : 'detailed';
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(VIEW_MODE_KEY, viewMode);
    } catch {
      // ignore quota / unavailable storage
    }
  }, [viewMode]);

  const parentUnit = String(item.unit ?? '').replace(/[()]/g, '') || t('Unit');

  // Atoms = the smallest base unit (gram / piece / ml). The parent's
  // quantity_in_unit_package is the number of atoms in one parent unit, and
  // every child's qty_in_package divides into the parent's (domain
  // invariant: fraction = child.qty_in_package / parent.qty_in_package).
  // Doing all arithmetic in integer atoms eliminates floating-point drift —
  // the inventory analog of "store money in cents".
  const ATOM_FACTOR = atomFactorOf(item);
  // Families with a weight-sold row (parent or child) keep three decimals
  // (1.5 kg): their atoms are scaled so the integer math above still holds.
  // 1 for everything else.
  const countScale = countScaleOf(item);
  const SCALED_FACTOR = ATOM_FACTOR * countScale;

  // Build the list of fraction items (parent + children) with expected stock
  // allocated greedily, largest pack first — pure integer math.
  const fractionItems: FractionCountItem[] = useMemo(() => {
    const rows: FractionCountItem[] = [];

    // Parent first (fraction = 1).
    rows.push({
      id: item.id,
      unit: String(item.unit ?? '').replace(/[()]/g, '') || t('Unit'),
      fraction: 1,
      expectedStock: 0,
      qty_in_package: Math.max(
        1,
        Math.round(item.quantity_in_unit_package ?? 1),
      ),
      is_sold_by_weight: item.is_sold_by_weight ?? false,
      unit_of_measurement: item.unit_of_measurement ?? '',
    });

    // Children (fraction < 1), excluding the parent itself, sorted desc.
    const childrenFractions = (item.store_product_fractions ?? [])
      .filter((f: StoreProductFraction) => f.id !== item.id && f.fraction < 1)
      .sort(
        (a: StoreProductFraction, b: StoreProductFraction) =>
          b.fraction - a.fraction,
      );

    for (const child of childrenFractions) {
      rows.push({
        id: child.id,
        unit:
          String(child.unit ?? '').replace(/[()]/g, '') ||
          child.fraction_name ||
          t('Fraction {{value}}', { value: child.fraction }),
        fraction: child.fraction,
        expectedStock: 0,
        qty_in_package: Math.max(
          1,
          Math.round(child.quantity_in_unit_package ?? 1),
        ),
        is_sold_by_weight: child.is_sold_by_weight ?? false,
        unit_of_measurement: item.unit_of_measurement ?? 'kg',
      });
    }

    // Decompose expected stock into counts per row, largest pack first.
    // A sub-pack residue lands as a decimal on a weight-sold row; for unit
    // products it is unreachable by definition.
    const expectedById = new Map(
      decomposeCount(Math.round(stock * SCALED_FACTOR), rows, countScale).map(
        (b) => [b.id, b.units],
      ),
    );

    return rows.map((r) => ({
      ...r,
      expectedStock: expectedById.get(r.id) ?? 0,
    }));
  }, [item, stock, SCALED_FACTOR, countScale, t]);

  // State to track quantities for each fraction item (keyed by id)
  const [quantities, setQuantities] = useState<Record<string, number>>({});

  // Initialize quantities when fractionItems change.
  // In blind mode, start at 0 so the user must enter a count from scratch.
  // Otherwise pre-fill with the expected allocation so the user only edits deltas.
  useEffect(() => {
    const initial: Record<string, number> = {};
    fractionItems.forEach((fi) => {
      initial[fi.id] = blindMode ? 0 : fi.expectedStock;
    });
    setQuantities(initial);
    setDrafts({});
  }, [fractionItems, blindMode]);

  // All sums run in (scaled) integer atoms; decimal input on weight-sold
  // rows (1.5 kg) is exact at three decimals.
  const actualAtoms = useMemo(
    () => sumCountAtoms(fractionItems, quantities, countScale),
    [fractionItems, quantities, countScale],
  );

  const expectedAtoms = Math.round(stock * SCALED_FACTOR);

  // Integer subtraction — no need to snap a sub-fraction residue to zero.
  const adjustmentAtoms = actualAtoms - expectedAtoms;

  // Convert back to display / payload units once, at the boundary.
  const actualStock = actualAtoms / SCALED_FACTOR;
  const adjustment = adjustmentAtoms / SCALED_FACTOR;
  const costDifference = (adjustmentAtoms * (item.cost ?? 0)) / SCALED_FACTOR;

  /** Decompose a (signed) atom value into units per row. */
  const decompose = useCallback(
    (atoms: number): BreakdownRow[] =>
      decomposeCount(atoms, fractionItems, countScale),
    [fractionItems, countScale],
  );

  const expectedBreakdown = useMemo(
    () => decompose(expectedAtoms),
    [decompose, expectedAtoms],
  );
  const countedBreakdown = useMemo(
    () => decompose(actualAtoms),
    [decompose, actualAtoms],
  );
  // Change is shown as per-row deltas (counted − expected) so the user sees
  // exactly what they edited, instead of a greedy re-decomposition of the
  // signed atom total (which can't represent mixed-sign rows).
  const changeBreakdown = useMemo<BreakdownRow[]>(
    () =>
      fractionItems.map((fi) => ({
        id: fi.id,
        name: fi.unit,
        units: roundQty((quantities[fi.id] ?? 0) - fi.expectedStock),
      })),
    [fractionItems, quantities],
  );

  // Grouped change = greedy decompose of the signed atom delta. Note this
  // can't represent mixed-sign per-row deltas — for that, switch to detailed.
  const changeGrouped = useMemo(
    () => decompose(adjustmentAtoms),
    [decompose, adjustmentAtoms],
  );

  // Detailed = per-row raw values. Counted only shows rows the user changed
  // from expected, so unchanged-from-expected rows fall away.
  const expectedDetailed = useMemo<BreakdownRow[]>(
    () =>
      fractionItems
        .filter((fi) => fi.expectedStock !== 0)
        .map((fi) => ({
          id: fi.id,
          name: fi.unit,
          units: fi.expectedStock,
        })),
    [fractionItems],
  );

  const countedDetailed = useMemo<BreakdownRow[]>(
    () =>
      fractionItems
        .filter((fi) => (quantities[fi.id] ?? 0) !== fi.expectedStock)
        .map((fi) => ({
          id: fi.id,
          name: fi.unit,
          units: quantities[fi.id] ?? 0,
        })),
    [fractionItems, quantities],
  );

  const renderSummary = (
    which: 'expected' | 'counted' | 'change',
    totalValue: number,
  ) => {
    if (viewMode === 'total') {
      return (
        <TotalLine
          value={totalValue}
          unit={parentUnit}
          signed={which === 'change'}
        />
      );
    }
    if (viewMode === 'grouped') {
      const data =
        which === 'expected'
          ? expectedBreakdown
          : which === 'counted'
            ? countedBreakdown
            : changeGrouped;
      return <BreakdownList breakdown={data} signed={which === 'change'} />;
    }
    const data =
      which === 'expected'
        ? expectedDetailed
        : which === 'counted'
          ? countedDetailed
          : changeBreakdown;
    return <BreakdownList breakdown={data} signed={which === 'change'} />;
  };
  const { isLoading, mutateAsync: createInventoryChange } = useMutation(
    'create-inventory-change',
    (data: CreateInventoryChange) =>
      apiAdapter.createInventoryChange({
        merchant_id: merchant.id,
        createInventoryChangeDto: data,
      }),
    {
      onSuccess: async () => {
        toast({
          title: t('Success'),
          description: t('Stock Updated Successfully!'),
          status: 'success',
        });

        // Update local Dexie DB so offline data stays fresh
        try {
          const updatedProduct = await apiAdapter.getStoreProduct({
            store_id: store.id,
            merchant_id: merchant.id,
            id: item.id,
          });
          productDb.storeProducts.put(updatedProduct);
        } catch {
          // Silently fail — local DB update is best-effort
        }

        onSuccess?.(item.id);
        onCounted?.();
        onClose();
      },
      onError: (error) => {
        if (isAxiosError(error)) {
          toast({
            title: t('Error'),
            description: error.response?.data.message,
            status: 'error',
          });
        }
      },
    },
  );

  // Raw text per input while it is being typed, so "1." or "0," is not
  // snapped back to a number mid-keystroke. Cleared on blur.
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  const handleQuantityChange = useCallback(
    (id: string, value: string) => {
      setDrafts((prev) => ({ ...prev, [id]: value }));
      if (value === '') {
        setQuantities((prev) => ({ ...prev, [id]: 0 }));
        return;
      }
      const parsed = parseFloat(value.replace(',', '.'));
      if (Number.isNaN(parsed) || parsed < 0) return;

      // Non-weight rows are counted in whole packs — floor any decimals
      // that sneak in via paste so atom math stays clean. Weight rows keep
      // three decimals.
      const row = fractionItems.find((fi) => fi.id === id);
      const normalized =
        row && !row.is_sold_by_weight ? Math.floor(parsed) : roundQty(parsed);

      setQuantities((prev) => ({ ...prev, [id]: normalized }));
    },
    [fractionItems],
  );

  const commitDraft = useCallback((id: string) => {
    setDrafts((prev) => {
      if (!(id in prev)) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleUpdateStock = async () => {
    if (!item.is_parent) {
      toast({
        title: t('Error'),
        description: t('This is a child product, you cannot update stock'),
        status: 'error',
      });
      return;
    }

    // Atom math is exact, so the counted stock is already the
    // canonical value to persist.
    const submittedStock = actualStock;

    if (onBeforeSubmit) {
      try {
        setIsSubmitting(true);
        await onBeforeSubmit(submittedStock);
      } catch {
        // Aborted by caller
        setIsSubmitting(false);
        return;
      }
      setIsSubmitting(false);
    }

    createInventoryChange({
      store_id: store.id,
      product_id: item.id,
      change_quantity: submittedStock,
      reason,
      merchant_id: merchant.id,
      source_id: source.id,
      cost: item.cost,
      source_type: EntityTypes.ACCOUNT,
      source_name: source.name,
      product_name: item.name + ' ' + item.unit,
      price: item.price,
      change_fraction: 1,
      inventory_value_change:
        (adjustmentAtoms * (item.cost ?? 0)) / SCALED_FACTOR,
      retail_value_change:
        (adjustmentAtoms * (item.price ?? 0)) / SCALED_FACTOR,
      source_created_at: new Date().toISOString(),
    });
  };

  const changeTone = adjustment < 0 ? 'red' : adjustment > 0 ? 'green' : 'gray';

  return (
    <Stack spacing={3}>
      {/* Drift alert — invisible when product data is clean */}
      <ProductDriftAlert
        variant="banner"
        product={item}
        merchant={merchant}
        store={store}
        updator={source}
      />

      {/* Header */}
      <Flex align="center" gap={3}>
        <Image
          src={item.image}
          w={10}
          h={10}
          objectFit="contain"
          borderRadius="md"
          flexShrink={0}
          fallback={<Box w={10} h={10} bg="gray.100" borderRadius="md" />}
        />
        <Text fontWeight="semibold" fontSize="sm" noOfLines={2} flex="1">
          {item.name}
        </Text>
        <ButtonGroup size="xs" isAttached variant="outline">
          <Tooltip label={t('Total')} openDelay={400}>
            <IconButton
              aria-label={t('Total view')}
              icon={<FiHash />}
              onClick={() => setViewMode('total')}
              colorScheme={viewMode === 'total' ? 'blue' : 'gray'}
              variant={viewMode === 'total' ? 'solid' : 'outline'}
            />
          </Tooltip>
          <Tooltip label={t('Grouped')} openDelay={400}>
            <IconButton
              aria-label={t('Grouped view')}
              icon={<FiLayers />}
              onClick={() => setViewMode('grouped')}
              colorScheme={viewMode === 'grouped' ? 'blue' : 'gray'}
              variant={viewMode === 'grouped' ? 'solid' : 'outline'}
            />
          </Tooltip>
          <Tooltip label={t('Detailed')} openDelay={400}>
            <IconButton
              aria-label={t('Detailed view')}
              icon={<FiList />}
              onClick={() => setViewMode('detailed')}
              colorScheme={viewMode === 'detailed' ? 'blue' : 'gray'}
              variant={viewMode === 'detailed' ? 'solid' : 'outline'}
            />
          </Tooltip>
        </ButtonGroup>
      </Flex>

      {/* Reason */}
      <RadioGroup
        value={reason}
        onChange={(r: string) => setReason(r as InventoryChangeReason)}>
        <Flex gap={4} justifyContent="center" flexWrap="wrap">
          <Radio value={InventoryChangeReason.INVENTORY_COUNT} size="sm">
            <Text fontSize="xs">{t('Count', { context: 'inventory' })}</Text>
          </Radio>
          <Radio value={InventoryChangeReason.INVENTORY_CORRECTION} size="sm">
            <Text fontSize="xs">{t('Correction')}</Text>
          </Radio>
        </Flex>
      </RadioGroup>

      {/* Pack-size rows */}
      <VStack spacing={1} align="stretch">
        {fractionItems.map((fi) => (
          <Flex
            key={fi.id}
            px={2.5}
            py={1.5}
            bg={fi.fraction === 1 ? 'blue.50' : 'gray.50'}
            borderRadius="md"
            borderLeft="3px solid"
            borderLeftColor={fi.fraction === 1 ? 'blue.600' : 'orange.400'}
            align="center"
            gap={2}>
            <Box flex="1" minW={0}>
              <Text fontWeight="medium" fontSize="sm" noOfLines={1}>
                {fi.unit}
              </Text>
              <Text fontSize="2xs" color="gray.500">
                {t('Qty in Pkg: {{qty}}', { qty: fi.qty_in_package ?? 1 })}
              </Text>
            </Box>
            {!blindMode && (
              <Text
                fontSize="xs"
                color="gray.500"
                whiteSpace="nowrap"
                sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {t('Expected:')} <strong>{fi.expectedStock}</strong>
              </Text>
            )}
            <Input
              type="number"
              min={0}
              step={fi.is_sold_by_weight ? 'any' : 1}
              inputMode={fi.is_sold_by_weight ? 'decimal' : 'numeric'}
              pattern={fi.is_sold_by_weight ? undefined : '\\d*'}
              value={drafts[fi.id] ?? quantities[fi.id] ?? 0}
              onChange={(e) => handleQuantityChange(fi.id, e.target.value)}
              onBlur={() => commitDraft(fi.id)}
              size="sm"
              w="64px"
              textAlign="center"
              bg="white"
              fontWeight="semibold"
            />
          </Flex>
        ))}
      </VStack>

      {/* Summary: Expected + Counted on row 1, Change full-width on row 2 */}
      <Grid templateColumns="repeat(2, 1fr)" gap={2}>
        {!blindMode && (
          <GridItem>
            <Box
              bg="gray.50"
              p={2}
              borderRadius="md"
              borderTop="2px solid"
              borderTopColor="gray.400"
              h="100%">
              <Text
                fontWeight="semibold"
                fontSize="2xs"
                color="gray.600"
                textTransform="uppercase"
                letterSpacing="wide"
                mb={1}>
                {t('Expected')}
              </Text>
              {renderSummary('expected', stock)}
            </Box>
          </GridItem>
        )}

        <GridItem colSpan={blindMode ? 2 : 1}>
          <Box
            bg="blue.50"
            p={2}
            borderRadius="md"
            borderTop="2px solid"
            borderTopColor="blue.400"
            h="100%">
            <Text
              fontWeight="semibold"
              fontSize="2xs"
              color="blue.700"
              textTransform="uppercase"
              letterSpacing="wide"
              mb={1}>
              {t('Counted')}
            </Text>
            {renderSummary('counted', actualStock)}
          </Box>
        </GridItem>

        {!blindMode && (
          <GridItem colSpan={2}>
            <Box
              bg={`${changeTone}.50`}
              p={2}
              borderRadius="md"
              borderTop="2px solid"
              borderTopColor={`${changeTone}.400`}>
              <Flex justify="space-between" align="center" mb={1}>
                <Text
                  fontWeight="semibold"
                  fontSize="2xs"
                  color={`${changeTone}.700`}
                  textTransform="uppercase"
                  letterSpacing="wide">
                  {t('Change')}
                </Text>
                {adjustment === 0 && (
                  <Badge colorScheme="gray" fontSize="2xs" variant="subtle">
                    {t('match')}
                  </Badge>
                )}
              </Flex>
              {renderSummary('change', adjustment)}
            </Box>
          </GridItem>
        )}
      </Grid>

      {!blindMode && (
        <Flex
          justifyContent="space-between"
          align="center"
          px={1}
          fontSize="sm">
          <Text color="gray.600">{t('Cost diff')}</Text>
          <Badge
            colorScheme={costDifference < 0 ? 'red' : 'gray'}
            variant="solid"
            fontSize="xs"
            px={2}
            py={0.5}
            borderRadius="md"
            sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {formatCurrency(Number(costDifference.toFixed(3)))}
          </Badge>
        </Flex>
      )}

      {/* Actions */}
      <Flex justifyContent="flex-end" gap={2} flexWrap="wrap">
        {showHistoryButton && onViewHistory && !blindMode && (
          <Button size="sm" variant="ghost" onClick={() => onViewHistory(item)}>
            {t('History')}
          </Button>
        )}
        <Button
          colorScheme="green"
          size="sm"
          flex={{ base: 1, sm: 'initial' }}
          onClick={handleUpdateStock}
          isLoading={isLoading || isSubmitting}>
          {t('Update Stock')}
        </Button>
      </Flex>
    </Stack>
  );
};
